import React, { useState } from "react";
import { Tooltip } from 'react-tooltip'
import ExpandableModal from "components/Model/ExpandableModal";
import { Button, Modal } from "react-bootstrap";
import { Image, Video } from "lucide-react";

const DynamicMasterTable = ({
  array,
  setExpandedRows,
  expandedRows,
  path = [],
  columns = []
}) => {
  const [mediaModal, setMediaModal] = useState({
    show: false,
    url: '',
    type: '', // 'image' or 'video'
    loading: true
  });


  const returnKey = (arr) => {
    let uniqueKey = [];
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    arr.forEach((obj) => {
      Object.keys(obj).forEach((element) => {
        if (user.role === 'admin') {
          if (element !== "_id" && !uniqueKey.includes(element)) {
            uniqueKey.push(element);
          }
        } else {
          if (
            element.slice(-4) !== 'Suid' &&
            element.slice(-4) !== 'suid' &&
            element !== "_id" &&
            !uniqueKey.includes(element)
          ) {
            uniqueKey.push(element);
          }
        }
      });
    });

    return uniqueKey;
  };


  const getHeaders = () => {
    const availableKeys = returnKey(array); // top-level keys only

    // Build column config map for show/hide and ordering
    const columnMap = (columns && columns.length > 0)
      ? columns.reduce((acc, col) => {
        const dataKey = col.originalName || col.key;
        if (dataKey) {
          acc[dataKey] = {
            newName: col.newName,
            isShow: col.isShow !== 'NO',
            order: col.order
          };
        }
        return acc;
      }, {})
      : {};

    // Map keys to headers applying config
    let headers = availableKeys
      .map(key => {
        const config = columnMap[key];

        if (columns && columns.length > 0 && config && config.isShow === false) {
          return null; // exclude hidden columns
        }

        return {
          key,
          name: config?.newName || key,
          isShow: true,
          order: config?.order
        };
      })
      .filter(Boolean);

    // Sort headers if order is defined
    const hasOrder = headers.some(h => h.order !== undefined);
    if (hasOrder) {
      headers = headers.sort((a, b) => {
        if (a.order === undefined) return 1;
        if (b.order === undefined) return -1;
        if (a.order === b.order) return a.key.localeCompare(b.key);
        return a.order - b.order;
      });
    }

    return headers;
  };

  const headers = getHeaders();

  if (array.length === 0) return null;

  // Function to check if a string is a URL
  const isUrl = (str) => {
    // Quick check for common URL patterns
    return /^https?:\/\/.+\..+/.test(str) ||
      /^\/\/.+\..+/.test(str) ||
      /^[^@]+@.+\..+/.test(str); // Handle cases with @ in path
  };

  // Improved image URL detection
  const isImageUrl = (url) => {
    const imageDomains = ['images.kgkdms.com', 'kgkdam.com'];
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];

    // First check extensions (works even with malformed URLs)
    const lowerUrl = url.toLowerCase();
    if (imageExtensions.some(ext => lowerUrl.includes(ext))) {
      return true;
    }

    // Then try proper URL parsing if possible
    try {
      // Fix common malformed URL patterns
      let fixedUrl = url;
      if (url.includes('@') && !url.includes('://')) {
        fixedUrl = url.replace('@', '://').replace(/\/\//g, '/');
      }

      const parsed = new URL(fixedUrl.includes('://') ? fixedUrl : `https://${fixedUrl}`);

      // Check if domain is in allowed list
      if (imageDomains.includes(parsed.hostname)) {
        return true;
      }

      // Check file extension again (in case URL was fixed)
      const pathname = parsed.pathname.toLowerCase();
      return imageExtensions.some(ext => pathname.endsWith(ext));
    } catch {
      return false;
    }
  };

  // Function to check if URL is a video
  const isVideoUrl = (url) => {
    return /\.(mp4|webm|ogg|mov)$/i.test(url);
  };

  const normalizeUrl = (url) => {
    if (!url) return url;

    // Fix common malformed patterns
    return url
      .replace(/([^:])\/\//g, '$1/')  // Fix double slashes
      .replace(/^([^/]+)@/, 'https://$1/') // Fix @ in domain
      .replace(/^\/\//, 'https://'); // Add protocol if missing
  };

  // Function to handle media preview
  const handleMediaPreview = (url) => {
    const normalizedUrl = normalizeUrl(url);
    if (isImageUrl(normalizedUrl)) {
      setMediaModal({
        show: true,
        url: normalizedUrl,
        type: 'image',
        loading: true
      });
    } else if (isVideoUrl(normalizedUrl)) {
      setMediaModal({
        show: true,
        url,
        type: 'video',
        loading: true
      });
    }
  };


  return (
    <>
      <table className="smartTable-table table table-striped border" style={{ fontSize: "0.9rem" }}>
        <thead className="head-level-1 thead-light">
          <tr className="Thead">
            {headers?.map((header, hIndex) => {
              const tooltipId = `tooltip-${path.join('-')}-${header.key}`;

              return (
                <th
                  key={`${header.key}-${hIndex}`}
                  className="font-weight-bold table-dynamic-fix"
                  style={{
                    backgroundColor: "#eff9ff",
                    textAlign: "left",
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  data-tooltip-id={tooltipId}
                  data-tooltip-content={header?.name.toUpperCase()}
                >
                  <div style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {header?.name.toUpperCase()}
                  </div>
                  <Tooltip
                    id={tooltipId}
                    style={{ zIndex: 9999, maxWidth: '300px' }}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {array.map((state, index) => (
            <tr className="border" key={index}>
              {headers.map((header, subIndex) => {
                const cellValue = state[header.key];
                const contractNo = headers.some(h => h.key === 'ContractNo_SKUNo') && state['ContractNo_SKUNo'];
                const isMediaUrl = typeof cellValue === 'string' && isUrl(cellValue) &&
                  (isImageUrl(cellValue) || isVideoUrl(cellValue));
                return (
                  <td
                    key={`${index}-${subIndex}`}
                    style={{
                      textAlign: (typeof cellValue === "string" || typeof cellValue === 'boolean') ? "left" : "right",
                      cursor: isMediaUrl ? 'pointer' : 'default'
                    }}
                    onClick={() => isMediaUrl && handleMediaPreview(cellValue)}
                  >
                    {isMediaUrl ? (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMediaPreview(cellValue);
                        }}
                      >
                        {isImageUrl(cellValue) ? <Image /> : <Video />}
                      </button>
                    ) : Array.isArray(cellValue) ? (
                      cellValue.length > 0 ? (
                        <ExpandableModal
                          title={`${header.key}`}
                          contractNo={contractNo}
                          content={
                            <DynamicMasterTable
                              array={cellValue}
                              expandedRows={expandedRows}
                              setExpandedRows={setExpandedRows}
                              path={[...path, index, header.key]}
                              columns={columns}
                            />
                          }
                        />
                      ) : (
                        <span>No data</span>
                      )
                    ) : typeof cellValue === "object" && cellValue !== null ? (
                      <ExpandableModal
                        title={`${header.key}`}
                        content={
                          <DynamicMasterTable
                            array={[cellValue]}
                            expandedRows={expandedRows}
                            setExpandedRows={setExpandedRows}
                            path={[...path, index, header.key]}
                            columns={columns}
                          />
                        }
                      />
                    ) : (
                      cellValue !== undefined ? (
                        typeof cellValue === 'boolean' ? cellValue.toString() : cellValue
                      ) : (
                        'N/A'
                      )
                    )}
                  </td>

                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <Modal
        show={mediaModal.show}
        onHide={() => setMediaModal({ ...mediaModal, show: false })}
        size="lg"
        centered
      >
        <Modal.Header>

          <Modal.Title>
            {mediaModal.type === 'image' ? 'Image Preview' : 'Video Preview'}
          </Modal.Title>
          <button className="btn-sm btn btn-outline-secondary" onClick={() => setMediaModal({ ...mediaModal, show: false })}>
            {'X'}
          </button>
        </Modal.Header>
        <Modal.Body className="text-center">
          {mediaModal.loading && (
            <div style={{ padding: '30px' }}>
              <div className="spinner-border text-primary" role="status" />
              <div style={{ marginTop: '10px' }}>Loading...</div>
            </div>
          )}

          {mediaModal.type === 'image' ? (
            mediaModal.url ? (
              <div
                style={{
                  maxHeight: '50vh',
                  display: mediaModal.loading ? 'none' : 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <img
                  src={mediaModal.url}
                  alt="Preview"
                  onLoad={() =>
                    setMediaModal((prev) => ({ ...prev, loading: false }))
                  }
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.textContent = "Path doesn't exist";
                    fallback.style.color = 'red';
                    fallback.style.fontWeight = 'bold';
                    fallback.style.textAlign = 'center';
                    e.target.parentNode.appendChild(fallback);
                    setMediaModal((prev) => ({ ...prev, loading: false }));
                  }}
                  style={{
                    maxWidth: '50%',
                    maxHeight: '40%',
                    objectFit: 'contain'
                  }}
                />
              </div>
            ) : (
              <div
                style={{
                  height: '50vh',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontWeight: 'bold',
                  color: 'gray'
                }}
              >
                Image not available
              </div>
            )
          ) : (
            <div style={{ display: mediaModal.loading ? 'none' : 'block' }}>
              <video
                controls
                onCanPlay={() =>
                  setMediaModal((prev) => ({ ...prev, loading: false }))
                }
                style={{ maxWidth: '100%', maxHeight: '70vh' }}
              >
                <source
                  src={mediaModal.url}
                  type={`video/${mediaModal.url.split('.').pop()}`}
                />
                Your browser does not support the video tag.
              </video>
            </div>
          )}
        </Modal.Body>


        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setMediaModal({ ...mediaModal, show: false })}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};


export default React.memo(DynamicMasterTable)